import React from 'react';
import type { UserProfile, ManualMember } from '../types';
import { cn } from '../utils/cn';

interface UserAvatarGroupProps {
    registeredUsers?: UserProfile[];
    manualUsers?: ManualMember[];
    maxDisplay?: number;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    onClick?: () => void;
    className?: string;
}

export const UserAvatarGroup: React.FC<UserAvatarGroupProps> = ({
    registeredUsers = [],
    manualUsers = [],
    maxDisplay = 5,
    size = 'sm',
    onClick,
    className
}) => {
    const totalUsers = registeredUsers.length + manualUsers.length;
    const displayUsers = [...registeredUsers, ...manualUsers].slice(0, maxDisplay);
    const overflowCount = totalUsers - maxDisplay;

    const sizeClasses = {
        xs: 'h-5 w-5 text-[9px]',
        sm: 'h-6 w-6 text-[10px]',
        md: 'h-8 w-8 text-xs',
        lg: 'h-10 w-10 text-sm'
    };

    const getInitials = (name: string) => {
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            const firstPart = parts[0] ?? '';
            const lastPart = parts[parts.length - 1] ?? '';
            return `${firstPart[0] ?? ''}${lastPart[0] ?? ''}`.toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const isManualUser = (user: UserProfile | ManualMember): user is ManualMember => {
        return 'addedAt' in user;
    };

    if (totalUsers === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex items-center gap-1",
                onClick && "cursor-pointer",
                className
            )}
            onClick={(e) => {
                if (onClick) {
                    e.stopPropagation();
                    onClick();
                }
            }}
        >
            <div className="flex -space-x-2">
                {displayUsers.map((user, index) => {
                    const isManual = isManualUser(user);
                    const name = isManual ? user.name : user.displayName;
                    const key = isManual ? user.id : user.uid;

                    return (
                        <div
                            key={key}
                            className={cn(
                                "flex items-center justify-center rounded-full border-2 border-white font-bold",
                                sizeClasses[size],
                                isManual
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-primary-100 text-primary-700"
                            )}
                            title={name}
                            style={{ zIndex: displayUsers.length - index }}
                        >
                            {getInitials(name)}
                        </div>
                    );
                })}
            </div>
            {overflowCount > 0 && (
                <span className="text-[10px] font-medium text-text-muted ml-1">
                    +{overflowCount} more
                </span>
            )}
        </div>
    );
};
